import type { JSX } from 'react';
import { AsyncState } from '@/components/async';
import {
  MobileFilterDrawer,
  type MobileFilterDrawerChip,
  type MobileFilterDrawerControl,
} from '@/components/dashboard/mobile/MobileFilterDrawer';
import { ErrorState } from '@/components/feedback';
import { PublicPageHeading } from '@/components/public/PublicPageHeading';
import { PublicUnitGroupListSkeleton, type AvailableUnitGroup } from '@/components/public/units';
import { Seo } from '@/components/seo';
import { PublicPageLayout, PublicPageSection } from '@/layouts/PublicPageLayout';
import type { PublicPropertyListResult } from '@/lib/types/publicProperty';
import type * as SiteSettingsTypes from '@/lib/types/siteSettings';
import { applyTemplate } from '@/lib/utils/textTemplates';
import type { AvailabilityFiltersState, RangeBounds } from '../availabilitiesFilters';
import { AvailabilityAlertsSignup } from './AvailabilityAlertsSignup';
import { AvailabilitiesDesktopFilters } from './AvailabilitiesDesktopFilters';
import { AvailabilitiesFilterToolbar } from './AvailabilitiesFilterToolbar';
import { AvailabilitiesResults } from './AvailabilitiesResults';
import { AvailabilitiesViewToggle, type AvailabilitiesViewMode } from './AvailabilitiesViewToggle';

interface PublicAvailabilitiesViewProps {
  availabilitiesContent: SiteSettingsTypes.AvailabilitiesPageContent;
  alertsContent: SiteSettingsTypes.AvailabilityAlertsContent | undefined;
  availabilityLabels: { immediateLabel: string; negotiableLabel: string };
  data: PublicPropertyListResult | null;
  isLoading: boolean;
  error: unknown;
  assetTypeOptions: string[];
  filters: AvailabilityFiltersState;
  onFiltersChange: (next: AvailabilityFiltersState) => void;
  activeFilterCount: number;
  hasActiveFilters: boolean;
  onResetFilters: () => void;
  viewMode: AvailabilitiesViewMode;
  onViewModeChange: (next: AvailabilitiesViewMode) => void;
  resultsSummary: string;
  groups: AvailableUnitGroup[];
  availableGroupsCount: number;
  summaryChips: MobileFilterDrawerChip[];
  drawerSummaryChips: MobileFilterDrawerChip[];
  drawerControls: MobileFilterDrawerControl[];
  isFilterDrawerOpen: boolean;
  onFilterDrawerChange: (open: boolean) => void;
  onDrawerApply: () => void;
  onDrawerReset: () => void;
  isApplyDisabled: boolean;
  monthlyRentBounds: RangeBounds | null;
}

export function PublicAvailabilitiesView({
  availabilitiesContent,
  alertsContent,
  availabilityLabels,
  data,
  isLoading,
  error,
  assetTypeOptions,
  filters,
  onFiltersChange,
  activeFilterCount,
  hasActiveFilters,
  onResetFilters,
  viewMode,
  onViewModeChange,
  resultsSummary,
  groups,
  availableGroupsCount,
  summaryChips,
  drawerSummaryChips,
  drawerControls,
  isFilterDrawerOpen,
  onFilterDrawerChange,
  onDrawerApply,
  onDrawerReset,
  isApplyDisabled,
  monthlyRentBounds,
}: PublicAvailabilitiesViewProps): JSX.Element {
  return (
    <PublicPageLayout>
      <Seo
        title={availabilitiesContent.seo.title}
        description={availabilitiesContent.seo.description}
        canonicalPath="/availabilities"
      />

      <PublicPageSection tone="base" spacing="lg">
        <PublicPageHeading
          as="h1"
          eyebrow={availabilitiesContent.heading.eyebrow}
          title={availabilitiesContent.heading.title}
          description={availabilitiesContent.heading.description}
          layout="stacked"
        />

        <div className="grid gap-6 md:gap-8 xl:grid-cols-[minmax(280px,340px)_minmax(0,1fr)] xl:items-start">
          <AvailabilitiesDesktopFilters
            filters={filters}
            onChange={onFiltersChange}
            assetTypeOptions={assetTypeOptions}
            activeFilterCount={activeFilterCount}
            hasActiveFilters={hasActiveFilters}
            onReset={onResetFilters}
            filtersContent={availabilitiesContent.filters}
            filterControlsContent={availabilitiesContent.filterControls}
            monthlyRentBounds={monthlyRentBounds}
          />

          <div className="flex flex-col gap-6">
            <AvailabilitiesViewToggle
              value={viewMode}
              onChange={onViewModeChange}
              labels={availabilitiesContent.viewToggle}
            />
            <p
              className="text-xs font-semibold uppercase tracking-wide text-text-primary/60"
              aria-live="polite"
            >
              {resultsSummary}
            </p>

            <div className="xl:hidden">
              <AvailabilitiesFilterToolbar
                hasActiveFilters={hasActiveFilters}
                activeFiltersLabel={applyTemplate(availabilitiesContent.filters.activeTemplate, {
                  count: activeFilterCount.toString(),
                })}
                titleLabel={availabilitiesContent.filters.title}
                subtitle={availabilitiesContent.filters.subtitle}
                emptyLabel={availabilitiesContent.filters.emptyLabel}
                resetLabel={availabilitiesContent.filters.resetLabel}
                openLabel={availabilitiesContent.filters.openLabel}
                onOpenDrawer={() => {
                  onFilterDrawerChange(true);
                }}
                onReset={onResetFilters}
                summaryChips={summaryChips}
              />
            </div>

            <AsyncState
              data={data}
              isLoading={isLoading}
              error={error}
              skeleton={<PublicUnitGroupListSkeleton groups={3} unitsPerGroup={3} />}
              errorFallback={
                <ErrorState description={availabilitiesContent.results.errorMessage} />
              }
              emptyFallback={
                <p className="rounded-2xl border border-dashed border-surface-subtle bg-surface-subtle/40 p-6 text-center text-sm text-text-primary/70">
                  {availabilitiesContent.results.noAvailabilityMessage}
                </p>
              }
            >
              {() => (
                <AvailabilitiesResults
                  groups={groups}
                  availableGroupsCount={availableGroupsCount}
                  hasActiveFilters={hasActiveFilters}
                  onResetFilters={onResetFilters}
                  viewMode={viewMode}
                  content={availabilitiesContent.results}
                  listContent={availabilitiesContent.list}
                  mapListContent={{
                    mapPanel: availabilitiesContent.mapPanel,
                    mapUnitRow: availabilitiesContent.mapUnitRow,
                    quickView: availabilitiesContent.quickView,
                    availabilityLabels,
                  }}
                />
              )}
            </AsyncState>

            <AvailabilityAlertsSignup id="availability-alerts" content={alertsContent} />
          </div>
        </div>

        <MobileFilterDrawer
          title={availabilitiesContent.filters.drawerTitle}
          open={isFilterDrawerOpen}
          onOpenChange={onFilterDrawerChange}
          summaryChips={drawerSummaryChips}
          controls={drawerControls}
          onApply={onDrawerApply}
          onReset={onDrawerReset}
          analyticsId="public-availabilities-filter-drawer"
          isApplyDisabled={isApplyDisabled}
          applyLabel={availabilitiesContent.filters.drawerApplyLabel}
          resetLabel={availabilitiesContent.filters.drawerResetLabel}
        />
      </PublicPageSection>
    </PublicPageLayout>
  );
}
